/**
 * external-data.ts
 * 
 * Servizio per la gestione dei dati esterni (clienti e articoli).
 * Allineato con le API del backend UfficioStile.
 */

import { apiClient, buildQueryParams } from './base';
import { 
  ExternalClient, 
  ExternalArticle,
  ApiResponse
} from '../../types/api';

export class ExternalDataService {
  private baseUrl = '/api/ExternalData';

  // ================== OPERAZIONI CLIENTI ==================
  async getClients(params?: { page?: number; pageSize?: number }): Promise<ExternalClient[]> {
    const queryString = params ? buildQueryParams(params) : '';
    const url = queryString ? `${this.baseUrl}/clients?${queryString}` : `${this.baseUrl}/clients`;
    const response = await apiClient.get<ExternalClient[]>(url);
    // Il backend restituisce direttamente l'array senza wrapper
    return Array.isArray(response) ? response : response.data || [];
  }

  async getClientById(externalId: string): Promise<ExternalClient> {
    const response = await apiClient.get<ExternalClient>(`${this.baseUrl}/clients/${externalId}`);
    return response.data || response;
  }

  async searchClients(query: string): Promise<ExternalClient[]> {
    
    const params = { searchTerm: query };
    const queryString = buildQueryParams(params);
    const response = await apiClient.get<ExternalClient[]>(`${this.baseUrl}/clients/search?${queryString}`);
    
    
    return Array.isArray(response) ? response : response.data || [];
  }

  // ================== OPERAZIONI ARTICOLI ==================
  async getArticles(params?: { page?: number; pageSize?: number }): Promise<ExternalArticle[]> {
    const queryString = params ? buildQueryParams(params) : '';
    const url = queryString ? `${this.baseUrl}/articles?${queryString}` : `${this.baseUrl}/articles`;
    const response = await apiClient.get<ExternalArticle[]>(url);
    return Array.isArray(response) ? response : response.data || [];
  }

  async getArticleById(externalId: string): Promise<ExternalArticle> {
    const response = await apiClient.get<ExternalArticle>(`${this.baseUrl}/articles/${externalId}`);
    return response.data || response;
  }

  async searchArticles(query: string): Promise<ExternalArticle[]> {
    const params = { searchTerm: query };
    const queryString = buildQueryParams(params);
    const response = await apiClient.get<ExternalArticle[]>(`${this.baseUrl}/articles/search?${queryString}`);
    return Array.isArray(response) ? response : response.data || [];
  }

  async getArticlesByCategory(category: string): Promise<ExternalArticle[]> {
    const response = await apiClient.get<ExternalArticle[]>(`${this.baseUrl}/articles/category/${category}`);
    return Array.isArray(response) ? response : response.data || [];
  }

  async getArticleCategories(): Promise<string[]> {
    const response = await apiClient.get<string[]>(`${this.baseUrl}/articles/categories`);
    return Array.isArray(response) ? response : response.data || [];
  }

  // ================== OPERAZIONI SISTEMA ==================
  async refreshCache(): Promise<void> {
    await apiClient.post(`${this.baseUrl}/refresh-cache`);
  }

  async testConnection(): Promise<{ isConnected: boolean; message: string }> {
    const response = await apiClient.get<{ isConnected: boolean; message: string }>(`${this.baseUrl}/test-connection`);
    return response.data || response;
  }

  async getStatistics(): Promise<any> {
    const response = await apiClient.get<any>(`${this.baseUrl}/statistics`);
    return response.data || response;
  }

  async getInfo(): Promise<any> {
    const response = await apiClient.get<any>(`${this.baseUrl}/info`);
    return response.data || response;
  }

  async syncAllExternalData(): Promise<void> {
    await apiClient.post(`${this.baseUrl}/sync-all`);
  }

  async getExternalDataStats(): Promise<any> {
    const response = await apiClient.get<any>(`${this.baseUrl}/stats`);
    return response.data || response;
  }

  // ================== METODI LEGACY PER COMPATIBILITÀ ==================
  async getExternalClients(params?: any): Promise<any> {
    return this.getClients(params);
  }

  async getExternalClientById(id: string): Promise<ExternalClient> {
    return this.getClientById(id);
  }

  async searchExternalClients(query: string, limit: number = 10): Promise<ExternalClient[]> {
    return this.searchClients(query);
  }

  async getExternalArticles(params?: any): Promise<any> {
    return this.getArticles(params);
  }

  async getExternalArticleById(id: string): Promise<ExternalArticle> {
    return this.getArticleById(id);
  }

  async searchExternalArticles(query: string, limit: number = 10): Promise<ExternalArticle[]> {
    return this.searchArticles(query);
  }
}

export const externalDataService = new ExternalDataService();
